import moment from 'moment-timezone'

export const ALL_YEARS = 'All Years'

const TIME_LOCALE_IDENTIFIERS = {
    en: 'en-US',
    zh: 'zh-CN',
    fr: 'fr-FR',
    de: 'de-DE',
    es: 'es-ES',
    ja: 'ja-JP',
    ms: 'ms-MY',
    id: 'id-ID',
    tl: 'fil-PH'
}

function timeLocaleIdentifier(locale) {
    const identifier = TIME_LOCALE_IDENTIFIERS[locale]
    if (identifier == null) {
        throw new Error('Time locale is invalid')
    }
    return identifier
}

export function timeEpoch(timestamp) {
    return Number(timestamp == null ? 0 : timestamp)
}

function timeDate(timestamp) {
    return new Date(timeEpoch(timestamp) * 1000)
}

export function parseTimeRates(rawValue) {
    if (rawValue === '') {
        return []
    }
    return rawValue.split('&&').map((entry, index) => {
        const parts = entry.split(':')
        if (parts.length !== 3 || parts[0] === '' || parts[1] === '' || !/^(0|1)$/.test(parts[2])) {
            throw new Error(`Invalid time rate at position ${index + 1}`)
        }
        const rate = Number(parts[1])
        if (!Number.isFinite(rate) || rate < 0) {
            throw new Error(`Invalid time rate amount at position ${index + 1}`)
        }
        return {name: parts[0], rate, showRateName: Number(parts[2])}
    })
}

export function encodeTimeRates(rates) {
    return rates.map(rate => `${rate.name}:${rate.rate}:${rate.showRateName}`).join('&&')
}

export function validateTimePreferences(company) {
    if (![0, 1, 2, 4].includes(company.timeGroupBy)) {
        throw new Error('Company timeGroupBy is invalid')
    }
    if (![0, 1].includes(company.timeSortType)) {
        throw new Error('Company timeSortType is invalid')
    }
    if (![0, 1].includes(company.timeSortAscending)) {
        throw new Error('Company timeSortAscending is invalid')
    }
}

export function makeTimeYearOptions(times) {
    const years = new Set(times.map(time => moment.unix(timeEpoch(time.startDate)).format('YYYY')))
    return [ALL_YEARS].concat(Array.from(years).sort((left, right) => Number(right) - Number(left)))
}

export function filterTimes(times, companyUUID, billed, year, keyword) {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    return times.filter(time => {
        if (time.companyUUID !== companyUUID || time.syncStatus === 1 || time.billed !== billed) {
            return false
        }
        if (year !== ALL_YEARS && moment.unix(timeEpoch(time.startDate)).format('YYYY') !== year) {
            return false
        }
        if (normalizedKeyword === '') {
            return true
        }
        return time.clientName.toLocaleLowerCase().includes(normalizedKeyword)
            || time.timeDescription.toLocaleLowerCase().includes(normalizedKeyword)
    })
}

export function timeGroupKey(time, groupBy, locale) {
    const date = moment.unix(timeEpoch(time.startDate))
    if (groupBy === 0) {
        return new Intl.DateTimeFormat(timeLocaleIdentifier(locale), {year: 'numeric'}).format(timeDate(time.startDate))
    }
    if (groupBy === 1) {
        const year = new Intl.DateTimeFormat(timeLocaleIdentifier(locale), {year: 'numeric'}).format(timeDate(time.startDate))
        return `Q${date.quarter()} ${year}`
    }
    if (groupBy === 2) {
        return new Intl.DateTimeFormat(timeLocaleIdentifier(locale), {year: 'numeric', month: 'long'}).format(timeDate(time.startDate))
    }
    return time.clientName
}

export function formatTimeListDate(timestamp, locale) {
    return new Intl.DateTimeFormat(timeLocaleIdentifier(locale), {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    }).format(timeDate(timestamp))
}

export function sortTimes(times, sortType, ascending) {
    const direction = ascending === 1 ? 1 : -1
    return times.slice().sort((left, right) => {
        if (sortType === 0) {
            const clientOrder = left.clientName.localeCompare(right.clientName)
            if (clientOrder !== 0) {
                return clientOrder * direction
            }
        }
        return (timeEpoch(left.startDate) - timeEpoch(right.startDate)) * direction
    })
}
